import React, { useRef } from 'react';
import { Prompt } from 'react-router-dom';
import { InlineSpinner } from '../../components/common/spinner';
import { Header } from '../../components/layout/header';
import { Footer } from '../../components/layout/footer';
import { TopicSelectionContent } from '../../components/topic-selection-content';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { useTopicPreferences, MAX_SELECTIONS } from '../../hooks/api/useTopicPreferencesPage';

export const TopicPreferencesPage: React.FC = () => {
  const toastNotificationContainerRef = useRef<HTMLDivElement>(null);
  const dfMdMedia = useMediaQuery('md');
  const topicPreferences = useTopicPreferences({
    mode: 'settings',
    toastNotificationContainerRef,
  });
  const {
    topicTagGroups,
    isLoading,
    selectedTagGroupIds,
    handleToggleTagGroup,
    layout,
    hasChanges,
    handleSave,
    isSaving,
  } = topicPreferences;

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <InlineSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Prompt
        when={hasChanges ?? false}
        message="You have unsaved changes. Are you sure you want to leave this page?"
      />
      <Header />
      <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
        <div className="max-w-page mx-auto px-6 py-8 md:py-12">
          <div className="mb-12">
            <h1 className="font-albert-sans text-3xl font-semibold leading-10 text-black">My Topic Preferences</h1>
          </div>
          <div className="flex flex-col items-center gap-12">
            <div className="flex w-full max-w-[630px] flex-col items-center gap-2">
              <h2 className="font-albert-sans text-2xl font-semibold leading-9 text-black">
                What are you most interested in?
              </h2>
              <p className="font-open-sans text-body-gray text-center text-base font-normal leading-6">
                Choose up to {MAX_SELECTIONS} topics and we will show you the most relevant stories and resources.
              </p>
            </div>
            <div className="w-full max-w-[630px]">
              <TopicSelectionContent
                topicTagGroups={topicTagGroups}
                isLoading={false}
                selectedTagGroupIds={selectedTagGroupIds}
                onToggleTagGroup={handleToggleTagGroup}
                maxSelections={MAX_SELECTIONS}
                useShortDescription={true}
                layout={layout}
                toastNotificationContainerRef={toastNotificationContainerRef}
              />
            </div>
            <div className="flex w-full max-w-[630px] items-center justify-center">
              <button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className="font-albert-sans bg-button-primary hover:bg-button-primary-hover flex w-full items-center justify-center rounded-full px-8 py-2.5 text-base font-medium leading-5 text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-300 md:w-[303px]"
              >
                {isSaving ? <span className="flex items-center gap-2">Saving...</span> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
